import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { User } from "../../../services/api";
import { useUsers } from "../../../hooks/useUsers";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import { lightTheme } from "../../../styles/theme";
import { phoneMatchesSearch } from "../../../utils/phoneUtils";
import { ActionModal, SearchField } from "../../shared";

interface LinkGuardianModalProps {
  open: boolean;
  onClose: () => void;
  /** Parent user currently being viewed — exclude from picker. */
  excludeUserIds: string[];
  onSelect: (user: User) => Promise<void> | void;
}

export const LinkGuardianModal: React.FC<LinkGuardianModalProps> = ({
  open,
  onClose,
  excludeUserIds,
  onSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const { data: users = [], isLoading, error } = useUsers({ enabled: open });

  const excluded = useMemo(
    () => new Set(excludeUserIds.filter(Boolean)),
    [excludeUserIds]
  );

  const filteredUsers = useMemo(() => {
    const available = users.filter((u) => !excluded.has(u.id));
    if (!searchQuery.trim()) return available;
    const q = searchQuery.toLowerCase();
    return available.filter(
      (user) =>
        user.name.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q) ||
        phoneMatchesSearch(user.phoneNumber, searchQuery)
    );
  }, [users, excluded, searchQuery]);

  const handleClose = () => {
    setSearchQuery("");
    setLocalError(null);
    setSubmittingId(null);
    onClose();
  };

  const handleSelect = async (user: User) => {
    setLocalError(null);
    setSubmittingId(user.id);
    try {
      await onSelect(user);
      handleClose();
    } catch (err: unknown) {
      setLocalError(
        err instanceof Error ? err.message : "Failed to link guardian"
      );
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <ActionModal
      open={open}
      onClose={handleClose}
      title="Link another parent"
      subtitle="Both parents will share this family member’s profile, fees, and activities."
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <SearchField
          size="small"
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by name, email, or phone..."
        />
        {error ? (
          <Alert severity="error">Failed to load users</Alert>
        ) : null}
        {localError ? <Alert severity="error">{localError}</Alert> : null}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No matching users found.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
            {filteredUsers.map((user) => (
              <ListItemButton
                key={user.id}
                disabled={!!submittingId}
                onClick={() => void handleSelect(user)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: getAvatarColor(user.name, lightTheme),
                      width: 36,
                      height: 36,
                      typography: "body2",
                    }}
                  >
                    {getInitials(user.name)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.name}
                  secondary={user.email || user.phoneNumber || user.id}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
                {submittingId === user.id ? (
                  <CircularProgress size={18} />
                ) : null}
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </ActionModal>
  );
};
